import React from 'react';
import OrigDatePicker from 'react-day-picker';
import startOfToday from 'date-fns/startOfToday';
import styled, { css } from 'styled-components';
import isBefore from 'date-fns/isBefore';
import isSameDay from 'date-fns/isSameDay';
import format from 'date-fns/format';

var capFirst = function capFirst(str) {
  return "" + str.charAt(0).toUpperCase() + str.slice(1);
};

var WEEKDAYS_SHORT = {
  nl: 'zo_ma_di_wo_do_vr_za'.split('_').map(capFirst),
  fr: 'Di_Lu_Ma_Me_Je_Ve_Sa'.split('_'),
  sv: 'sö_må_ti_ons_to_fre_lö'.split('_').map(capFirst),
  fi: 'su_ma_ti_ke_to_pe_la'.split('_').map(capFirst),
  de: 'So._Mo._Di._Mi._Do._Fr._Sa.'.split('_'),
  ru: 'вс_пн_вт_ср_чт_пт_сб'.split('_').map(capFirst),
  es: 'dom._lun._mar._mié._jue._vie._sáb.'.split('_').map(capFirst),
  'es-US': 'dom._lun._mar._mié._jue._vie._sáb.'.split('_').map(capFirst),
  pt: 'Dom_Seg_Ter_Qua_Qui_Sex_Sáb'.split('_'),
  it: 'dom_lun_mar_mer_gio_ven_sab'.split('_').map(capFirst),
  ca: 'dg._dl._dt._dc._dj._dv._ds.'.split('_').map(capFirst),
  pl: 'nd_pn_wt_śr_czw_pt_sob'.split('_').map(capFirst),
  cs: 'ne_po_út_st_čt_pá_so'.split('_').map(capFirst),
  mt: 'Ħa_Tn_Tl_Er_Ħa_Ġi_Si'.split('_'),
  el: 'Κυρ_Δευ_Τρι_Τετ_Πεμ_Παρ_Σαβ'.split('_'),
  sq: 'Die_Hën_Mar_Mër_Enj_Pre_Sht'.split('_'),
  sl: 'Ned_Pon_Tor_Sre_Čet_Pet_Sob'.split('_'),
  hr: 'Ned_Pon_Uto_Sri_Čet_Pet_Sub'.split('_'),
  zh: '星期日_星期一_星期二_星期三_星期四_星期五_星期六'.split('_'),
  ar: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
  'ar-SA': ['السبت', 'الجمعة', 'الخميس', 'الأربعاء', 'الثلاثاء', 'الإثنين', 'الأحد']
};
var WEEKDAYS_LONG = {
  nl: 'zondag_maandag_dinsdag_woensdag_donderdag_vrijdag_zaterdag'.split('_').map(capFirst),
  fr: 'dimanche_lundi_mardi_mercredi_jeudi_vendredi_samedi'.split('_').map(capFirst),
  sv: 'söndag_måndag_tisdag_onsdag_torsdag_fredag_lördag'.split('_').map(capFirst),
  fi: 'sunnuntai_maanantai_tiistai_keskiviikko_torstai_perjantai_lauantai'.split('_').map(capFirst),
  de: 'Sonntag_Montag_Dienstag_Mittwoch_Donnerstag_Freitag_Samstag'.split('_'),
  ru: 'воскресенье_понедельник_вторник_среда_четверг_пятница_суббота'.split('_').map(capFirst),
  es: 'domingo_lunes_martes_miércoles_jueves_viernes_sábado'.split('_').map(capFirst),
  'es-US': 'domingo_lunes_martes_miércoles_jueves_viernes_sábado'.split('_').map(capFirst),
  pt: 'Domingo_Segunda-feira_Terça-feira_Quarta-feira_Quinta-feira_Sexta-feira_Sábado'.split('_'),
  it: 'domenica_lunedì_martedì_mercoledì_giovedì_venerdì_sabato'.split('_').map(capFirst),
  ca: 'diumenge_dilluns_dimarts_dimecres_dijous_divendres_dissabte'.split('_').map(capFirst),
  pl: 'niedziela_poniedziałek_wtorek_środa_czwartek_piątek_sobota'.split('_').map(capFirst),
  cs: 'neděle_pondělí_úterý_středa_čtvrtek_pátek_sobota'.split('_').map(capFirst),
  mt: 'Il-Ħadd_It-Tnejn_It-Tlieta_L-Erbgħa_Il-Ħamis_Il-Ġimgħa_Is-Sibt'.split('_'),
  el: 'Κυριακή_Δευτέρα_Τρίτη_Τετάρτη_Πέμπτη_Παρασκευή_Σάββατο'.split('_'),
  sq: 'E Diel_E Hënë_E Martë_E Mërkurë_E Enjte_E Premte_E Shtunë'.split('_'),
  sl: 'Nedelja_Ponedeljek_Torek_Sreda_Četrtek_Petek_Sobota'.split('_'),
  hr: 'Nedjelja_Ponedjeljak_Utorak_Srijeda_Četvrtak_Petak_Subota'.split('_'),
  zh: '星期日_星期一_星期二_星期三_星期四_星期五_星期六'.split('_'),
  ar: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
  'ar-SA': ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
};
var MONTHS = {
  nl: 'januari_februari_maart_april_mei_juni_juli_augustus_september_oktober_november_december'.split('_').map(capFirst),
  fr: 'janvier_février_mars_avril_mai_juin_juillet_août_septembre_octobre_novembre_décembre'.split('_').map(capFirst),
  sv: 'januari_februari_mars_april_maj_juni_juli_augusti_september_oktober_november_december'.split('_').map(capFirst),
  fi: 'tammikuu_helmikuu_maaliskuu_huhtikuu_toukokuu_kesäkuu_heinäkuu_elokuu_syyskuu_lokakuu_marraskuu_joulukuu'.split('_').map(capFirst),
  de: 'Januar_Februar_März_April_Mai_Juni_Juli_August_September_Oktober_November_Dezember'.split('_'),
  ru: 'январь_февраль_март_апрель_май_июнь_июль_август_сентябрь_октябрь_ноябрь_декабрь'.split('_').map(capFirst),
  es: 'enero_febrero_marzo_abril_mayo_junio_julio_agosto_septiembre_octubre_noviembre_diciembre'.split('_').map(capFirst),
  'es-US': 'enero_febrero_marzo_abril_mayo_junio_julio_agosto_septiembre_octubre_noviembre_diciembre'.split('_').map(capFirst),
  pt: 'Janeiro_Fevereiro_Março_Abril_Maio_Junho_Julho_Agosto_Setembro_Outubro_Novembro_Dezembro'.split('_'),
  it: 'gennaio_febbraio_marzo_aprile_maggio_giugno_luglio_agosto_settembre_ottobre_novembre_dicembre'.split('_').map(capFirst),
  ca: 'gener_febrer_març_abril_maig_juny_juliol_agost_setembre_octubre_novembre_desembre'.split('_').map(capFirst),
  pl: 'Styczeń_Luty_Marzec_Kwiecień_Maj_Czerwiec_Lipiec_Sierpień_Wrzesień_Październik_Listopad_Grudzień'.split('_'),
  cs: 'leden_únor_březen_duben_květen_červen_červenec_srpen_září_říjen_listopad_prosinec'.split('_').map(capFirst),
  mt: 'Jannar_Frar_Marzu_April_Mejju_Ġunju_Lulju_Awwissu_Settembru_Ottubru_Novembru_Diċembru'.split('_'),
  el: 'Ιανουάριος_Φεβρουάριος_Μάρτιος_Απρίλιος_Μάιος_Ιούνιος_Ιούλιος_Αύγουστος_Σεπτέμβριος_Οκτώβριος_Νοέμβριος_Δεκέμβριος'.split('_'),
  sq: 'Janar_Shkurt_Mars_Prill_Maj_Qershor_Korrik_Gusht_Shtator_Tetor_Nëntor_Dhjetor'.split('_'),
  sl: 'Januar_Februar_Marec_April_Maj_Junij_Julij_Avgust_September_Oktober_November_December'.split('_'),
  hr: 'Siječanj_Veljača_Ožujak_Travanj_Svibanj_Lipanj_Srpanj_Kolovoz_Rujan_Listopad_Studeni_Prosinac'.split('_'),
  zh: '一月_二月_三月_四月_五月_六月_七月_八月_九月_十月_十一月_十二月'.split('_'),
  ar: ['يناير', 'فبراير', 'مارس', 'إبريل', 'مايو', 'يونيه', 'يوليه', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
  'ar-SA': ['يناير', 'فبراير', 'مارس', 'إبريل', 'مايو', 'يونيه', 'يوليه', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
};
var LABELS = {
  nl: {
    nextMonth: 'volgende maand',
    previousMonth: 'vorige maand'
  },
  fr: {
    nextMonth: 'le mois précédent',
    previousMonth: 'le mois prochain'
  },
  sv: {
    nextMonth: 'nästa månad',
    previousMonth: 'förra månaden'
  },
  fi: {
    nextMonth: 'ensikuussa',
    previousMonth: 'edellinen kuukausi'
  },
  de: {
    nextMonth: 'nächsten Monat',
    previousMonth: 'vorheriger Monat'
  },
  ru: {
    nextMonth: 'следующий месяц',
    previousMonth: 'предыдущий месяц'
  },
  es: {
    nextMonth: 'próximo mes',
    previousMonth: 'mes anterior'
  },
  'es-US': {
    nextMonth: 'próximo mes',
    previousMonth: 'mes anterior'
  },
  pt: {
    nextMonth: 'próximo mês',
    previousMonth: 'mês anterior'
  },
  it: {
    nextMonth: 'il prossimo mese',
    previousMonth: 'il mese scorso'
  },
  ca: {
    nextMonth: 'proxim mes',
    previousMonth: 'mes anterior'
  },
  pl: {
    nextMonth: 'poprzedni miesiąc',
    previousMonth: 'w przyszłym miesiącu'
  },
  cs: {
    nextMonth: 'příští měsíc',
    previousMonth: 'předchozí měsíc'
  },
  mt: {
    nextMonth: 'Il-ġejjieni xahar',
    previousMonth: 'Ix-xahar li għadda'
  },
  el: {
    nextMonth: 'τον επόμενο μήνα',
    previousMonth: 'τον προηγούμενο μήνα'
  },
  sq: {
    nextMonth: 'muaji i ardhshëm',
    previousMonth: 'muaji i kaluar'
  },
  sl: {
    nextMonth: 'naslednji mesec',
    previousMonth: 'prejšnji mesec'
  },
  hr: {
    nextMonth: 'sljedeći mjesec',
    previousMonth: 'prošli mjesec'
  },
  zh: {
    nextMonth: '下月',
    previousMonth: '上月'
  },
  ar: {
    nextMonth: 'الشهر المقبل',
    previousMonth: 'الشهر الماضى'
  },
  'ar-SA': {
    nextMonth: 'الشهر المقبل',
    previousMonth: 'الشهر الماضى'
  }
};

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  strings.raw = raw;
  return strings;
}

function _templateObject3() {
  var data = _taggedTemplateLiteralLoose(["\n  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;\n  ", "\n"]);

  _templateObject3 = function _templateObject3() {
    return data;
  };

  return data;
}

function _templateObject2() {
  var data = _taggedTemplateLiteralLoose(["\n            & .DayPicker-Day--disabled:not(.DayPicker-Day--past):hover {\n              opacity: 1;\n            }\n            & .DayPicker-Day--disabled:not(.DayPicker-Day--past):hover::before {\n              display: block;\n              position: absolute;\n              z-index: 300;\n              top: -35px;\n              left: 50%;\n              transform: translateX(-50%);\n              height: 40px;\n              width: 180px;\n              border-radius: 8px;\n              color: #ccd8db;\n              font-size: 13px;\n              line-height: 40px;\n              background-color: #596a76;\n            }\n          "]);

  _templateObject2 = function _templateObject2() {
    return data;
  };

  return data;
}

function _templateObject() {
  var data = _taggedTemplateLiteralLoose(["\n  outline: none !important;\n  & *:focus {\n    outline: none !important;\n  }\n  & .DayPicker-wrapper {\n    position: relative;\n    z-index: 10;\n    -webkit-user-select: none;\n    -moz-user-select: none;\n    -ms-user-select: none;\n    user-select: none;\n    padding-bottom: 1rem;\n    -webkit-box-orient: horizontal;\n    -webkit-box-direction: normal;\n    -ms-flex-direction: row;\n    flex-direction: row;\n  }\n  & .DayPicker-Months {\n    display: -webkit-box;\n    display: -ms-flexbox;\n    display: flex;\n    -ms-flex-wrap: wrap;\n    flex-wrap: wrap;\n    -webkit-box-pack: center;\n    -ms-flex-pack: center;\n    justify-content: center;\n  }\n  & .DayPicker-Month {\n    display: table;\n    border-collapse: collapse;\n    border-spacing: 0;\n    -webkit-user-select: none;\n    -moz-user-select: none;\n    -ms-user-select: none;\n    user-select: none;\n    margin: 0;\n    width: 100%;\n    max-width: ", ";\n    background: #fff;\n    border-radius: ", ";\n    box-shadow: ", ";\n    border: ", ";\n  }\n  & .DayPicker-NavButton {\n    position: absolute;\n    cursor: pointer;\n    top: 0;\n    margin-top: 9px;\n    color: #8b9898;\n    width: 1.25rem;\n    height: 1.25rem;\n    display: inline-block;\n    background-size: 65%;\n    background-repeat: no-repeat;\n    background-position: center;\n    &:first-child {\n      ", "\n      ", "\n    }\n    &:last-child {\n      ", "\n      ", "\n    }\n  }\n  & .DayPicker-NavButton:hover {\n    opacity: 0.8;\n  }\n  & .DayPicker-NavButton--prev {\n    margin-right: 1.5rem;\n    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPHN2ZyB3aWR0aD0iMTc5MiIgaGVpZ2h0PSIxNzkyIiB2aWV3Qm94PSIwIDAgMTc5MiAxNzkyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxwYXRoIGZpbGw9IiM3Rjg4ODkiIGQ9Ik0xNDI3IDMwMWwtNTMxIDUzMSA1MzEgNTMxcTE5IDE5IDE5IDQ1dC0xOSA0NWwtMTY2IDE2NnEtMTkgMTktNDUgMTl0LTQ1LTE5bC03NDItNzQycS0xOS0xOS0xOS00NXQxOS00NWw3NDItNzQycTE5LTE5IDQ1LTE5dDQ1IDE5bDE2NiAxNjZxMTkgMTkgMTkgNDV0LTE5IDQ1eiIvPjwvc3ZnPg==');\n  }\n  & .DayPicker-NavButton--next {\n    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPHN2ZyB3aWR0aD0iMTc5MiIgaGVpZ2h0PSIxNzkyIiB2aWV3Qm94PSIwIDAgMTc5MiAxNzkyIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxwYXRoIGZpbGw9IiM3Rjg4ODkiIGQ9Ik0xMzYzIDg3N2wtNzQyIDc0MnEtMTkgMTktNDUgMTl0LTQ1LTE5bC0xNjYtMTY2cS0xOS0xOS0xOS00NXQxOS00NWw1MzEtNTMxLTUzMS01MzFxLTE5LTE5LTE5LTQ1dDE5LTQ1bDE2Ni0xNjZxMTktMTkgNDUtMTl0NDUgMTlsNzQyIDc0MnExOSAxOSAxOSA0NXQtMTkgNDV6Ii8+PC9zdmc+');\n  }\n  & .DayPicker-NavButton--interactionDisabled {\n    display: none;\n  }\n  & .DayPicker-Caption {\n    padding: 0;\n    display: table-caption;\n    text-align: center;\n    margin: 10px 0;\n  }\n  & .DayPicker-Caption > div {\n    font-size: 1rem;\n  }\n  & .DayPicker-Weekdays {\n    margin-top: 1rem;\n    display: table-header-group;\n  }\n  & .DayPicker-WeekdaysRow {\n    display: table-row;\n  }\n  & .DayPicker-Weekday {\n    display: table-cell;\n    padding: 0.5rem;\n    font-size: 12px;\n    text-align: center;\n    color: #8b9898;\n  }\n  & .DayPicker-Weekday abbr[title] {\n    border-bottom: none;\n    text-decoration: none;\n  }\n  & .DayPicker-Body {\n    display: table-row-group;\n  }\n  & .DayPicker-Week {\n    display: table-row;\n    border-top: 1px solid #edefef;\n    &:first-child {\n      border-top: none;\n    }\n  }\n  & .DayPicker-Day {\n    display: table-cell;\n    padding: 0.5rem 0.75rem;\n    text-align: center;\n    vertical-align: middle;\n    outline: none;\n    border-right: 1px solid #edefef;\n    &:last-child {\n      border-right: none;\n    }\n    &:not(.DayPicker-Day--selected):not(.DayPicker-Day--past) {\n      cursor: pointer;\n    }\n    &.DayPicker-Day--past {\n      pointer-events: none;\n    }\n  }\n  .DayPicker-Day--today:not(.DayPicker-Day--disabled) & {\n    border-bottom: 2px solid ", " !important;\n  }\n  & .DayPicker-WeekNumber {\n    display: table-cell;\n    padding: 0.5rem;\n    text-align: right;\n    vertical-align: middle;\n    min-width: 1rem;\n    font-size: 0.75em;\n    cursor: pointer;\n    color: #8b9898;\n    border-right: 1px solid #eaecec;\n  }\n  & .DayPicker--interactionDisabled .DayPicker-Day {\n    cursor: default;\n  }\n  & .DayPicker-Footer {\n    padding-top: 0.5rem;\n  }\n  & .DayPicker-TodayButton {\n    border: none;\n    background-image: none;\n    background-color: transparent;\n    -webkit-box-shadow: none;\n    box-shadow: none;\n    cursor: pointer;\n    color: #4a90e2;\n    font-size: 0.875em;\n  }\n  & .DayPicker-Day--outside,\n  & .DayPicker-Day--disabled {\n    cursor: default;\n  }\n  & .DayPicker-Day--sunday {\n    background-color: #f7f8f8;\n  }\n  & .DayPicker-Day--sunday:not(.DayPicker-Day--today) {\n    color: #dce0e0;\n  }\n  &\n    .DayPicker-Day--selected:not(.DayPicker-Day--disabled):not(.DayPicker-Day--outside) {\n    position: relative;\n    color: #f0f8ff;\n    border-radius: 100%;\n    z-index: 1;\n    background: transparent;\n  }\n  & .DayPickerInput-OverlayWrapper {\n    .DayPicker-Day {\n      padding: 0.5rem 0.75rem !important;\n    }\n    .DayPicker-Day--selected:after {\n      content: '';\n      display: block;\n      position: absolute;\n      left: 50%;\n      border-radius: 50%;\n      width: 30px;\n      height: 30px;\n      margin: auto;\n      top: 0;\n      left: 0;\n      right: 0;\n      bottom: 0;\n      background-color: ", ";\n      z-index: -1;\n    }\n  }\n  .DayPickerInput-Overlay > .DayPicker-Day {\n    padding: 0.5rem 0.75rem !important;\n  }\n  &\n    .DayPicker:not(.DayPicker--interactionDisabled)\n    .DayPicker-Day:not(.DayPicker-Day--disabled):not(.DayPicker-Day--selected):not(.DayPicker-Day--past)\n    :not(.DayPicker-Day--outside):hover {\n    background-color: #e2e8ea;\n  }\n  .DayPicker-Day:not(.DayPicker-Day--selected):not(.DayPicker-Day--past):hover {\n    background: #e2e8ea;\n    & .day {\n      font-weight: bold;\n    }\n  }\n  /* DayPickerInput */\n  & .DayPickerInput {\n    width: 100%;\n    display: inline-block;\n  }\n  & .DayPickerInput-OverlayWrapper {\n    position: relative;\n  }\n  & .DayPickerInput-Overlay {\n    left: 0;\n    z-index: 1;\n    position: absolute;\n  }\n  & .DayPicker {\n    display: block;\n    color: ", ";\n    border-radius: ", ";\n    & .DayPicker-Day {\n      padding: 0;\n      position: relative;\n    }\n    & .DayPicker-NavButton {\n      color: ", ";\n    }\n    & .DayPicker-Day--disabled {\n      font-weight: normal;\n      & * {\n        font-weight: normal;\n      }\n    }\n    ", "\n  }\n"]);

  _templateObject = function _templateObject() {
    return data;
  };

  return data;
}
var calendarCss = css(_templateObject(), function (props) {
  var _props$theme;

  return ((_props$theme = props.theme) === null || _props$theme === void 0 ? void 0 : _props$theme.maxWidth) || '480px';
}, function (props) {
  var _props$theme2;

  return ((_props$theme2 = props.theme) === null || _props$theme2 === void 0 ? void 0 : _props$theme2.borderRadius) || '6px';
}, function (props) {
  var _props$theme3;

  return ((_props$theme3 = props.theme) === null || _props$theme3 === void 0 ? void 0 : _props$theme3.borderShadow) || '0 3px 4px 0 rgba(0, 0, 0, 0.1)';
}, function (props) {
  var _props$theme4;

  return ((_props$theme4 = props.theme) === null || _props$theme4 === void 0 ? void 0 : _props$theme4.border) || '1px';
}, function (p) {
  return p.theme.reverse ? 'right: 1.5rem;' : 'left: 1.5rem;';
}, function (p) {
  return p.theme.reverse ? 'transform: rotate(180deg);' : '';
}, function (p) {
  return p.theme.reverse ? 'left: 1.5rem;' : 'right: 1.5rem;';
}, function (p) {
  return p.theme.reverse ? 'transform: rotate(180deg);' : '';
}, function (p) {
  var _p$theme;

  return ((_p$theme = p.theme) === null || _p$theme === void 0 ? void 0 : _p$theme.primaryColor) || '#9369BD';
}, function (props) {
  var _props$theme5;

  return ((_props$theme5 = props.theme) === null || _props$theme5 === void 0 ? void 0 : _props$theme5.selectedColor) || '#4A90E2';
}, function (props) {
  var _props$theme6;

  return ((_props$theme6 = props.theme) === null || _props$theme6 === void 0 ? void 0 : _props$theme6.greyColor) || '#26323A';
}, function (props) {
  var _props$theme7;

  return ((_props$theme7 = props.theme) === null || _props$theme7 === void 0 ? void 0 : _props$theme7.borderRadius) || '6px';
}, function (props) {
  var _props$theme8;

  return ((_props$theme8 = props.theme) === null || _props$theme8 === void 0 ? void 0 : _props$theme8.primaryColor) || '#9369BD';
}, function (props) {
  var _props$theme9;

  return !props.theme || ((_props$theme9 = props.theme) === null || _props$theme9 === void 0 ? void 0 : _props$theme9.isDesktop) ? css(_templateObject2()) : '';
});
var CalendarStyleWrapper = styled.div(_templateObject3(), calendarCss);

function _templateObject$1() {
  var data = _taggedTemplateLiteralLoose(["\n  & > .day {\n    display: flex;\n    flex-direction: column;\n    align-content: center;\n    justify-content: center;\n    width: 30px;\n    height: 30px;\n    text-align: center;\n    margin: 0 auto;\n    :not(.DayPicker-Day--disabled) & {\n      color: #7f8889;\n    }\n    .DayPicker-Day--today:not(.DayPicker-Day--disabled) & {\n      font-weight: 700;\n    }\n  }\n  .DayPicker-Day--today:not(.DayPicker-Day--disabled) & {\n    border-bottom: 2px solid ", " !important;\n  }\n  .DayPicker-Day--today + .DayPicker-Day:not(.DayPicker-Day--disabled) & {\n    border-bottom: 2px solid\n      ", " !important;\n  }\n  .DayPicker-Day & .day {\n    opacity: 1;\n  }\n  .DayPicker-Day--outside & .day {\n    opacity: 0.8;\n  }\n  .DayPicker-Day--past & .day,\n  .DayPicker-Day--disabled & .day {\n    opacity: 0.25;\n  }\n  .DayPicker-Day & {\n    border-bottom: 2px solid transparent;\n  }\n  .DayPicker-Day--today:not(.DayPicker-Day--disabled) & {\n    border-bottom: 2px solid\n      ", ";\n  }\n  .DayPicker-Day--today + .DayPicker-Day:not(.DayPicker-Day--disabled) & {\n    border-bottom: 2px solid\n      ", ";\n  }\n  .DayPicker-Day--today & {\n    color: ", ";\n  }\n  & > .price {\n    font-size: 11px;\n    line-height: 20px;\n    text-align: center;\n  }\n  .DayPicker-Day {\n    padding: 0;\n  }\n  .DayPicker-Day--selected:not(.DayPicker-Day--disabled) & {\n    background: ", ";\n    & * {\n      color: ", " !important;\n      font-weight: bold;\n    }\n  }\n"]);

  _templateObject$1 = function _templateObject() {
    return data;
  };

  return data;
}
var DayCell = styled.div(_templateObject$1(), function (p) {
  var _p$theme;

  return ((_p$theme = p.theme) === null || _p$theme === void 0 ? void 0 : _p$theme.primaryColor) || '#9369BD';
}, function (p) {
  var _p$theme2;

  return (((_p$theme2 = p.theme) === null || _p$theme2 === void 0 ? void 0 : _p$theme2.primaryColor) || '#9369BD') + '50';
}, function (props) {
  var _props$theme;

  return ((_props$theme = props.theme) === null || _props$theme === void 0 ? void 0 : _props$theme.primaryColor) || '#9369BD';
}, function (props) {
  var _props$theme2;

  return ((_props$theme2 = props.theme) === null || _props$theme2 === void 0 ? void 0 : _props$theme2.primaryColor) || '#9369BD';
}, function (props) {
  var _props$theme3;

  return ((_props$theme3 = props.theme) === null || _props$theme3 === void 0 ? void 0 : _props$theme3.greyColor) || '#26323A';
}, function (props) {
  var _props$theme4;

  return props.highLightColor ? props.highLightColor : ((_props$theme4 = props.theme) === null || _props$theme4 === void 0 ? void 0 : _props$theme4.primaryColor) || '#9369BD';
}, function (p) {
  var _p$theme3;

  return ((_p$theme3 = p.theme) === null || _p$theme3 === void 0 ? void 0 : _p$theme3.whiteColor) || '#FFFFFF';
});

var getColorFromWeight = function getColorFromWeight(weight, theme) {
  if (weight === void 0) {
    weight = 0;
  }

  var dayColor;

  if (!weight || weight === 0) {
    var _theme$na_color;

    dayColor = (theme === null || theme === void 0 ? void 0 : (_theme$na_color = theme.na_color) === null || _theme$na_color === void 0 ? void 0 : _theme$na_color.flat().css()) || '#8B9898';
  } else if (weight >= 1 && weight <= 3) {
    var _theme$first_band;

    dayColor = (theme === null || theme === void 0 ? void 0 : (_theme$first_band = theme.first_band) === null || _theme$first_band === void 0 ? void 0 : _theme$first_band.flat().css()) || '#00AC1E';
  } else if (weight >= 4 && weight <= 6) {
    var _theme$second_band;

    dayColor = (theme === null || theme === void 0 ? void 0 : (_theme$second_band = theme.second_band) === null || _theme$second_band === void 0 ? void 0 : _theme$second_band.flat().css()) || '#00AC1E';
  } else if (weight >= 7 && weight <= 10) {
    var _theme$third_band;

    dayColor = (theme === null || theme === void 0 ? void 0 : (_theme$third_band = theme.third_band) === null || _theme$third_band === void 0 ? void 0 : _theme$third_band.flat().css()) || '#FFAC42';
  }

  return dayColor;
};

var RtpCalendarDayCell = function RtpCalendarDayCell(_ref) {
  var date = _ref.date,
      prices = _ref.prices,
      _ref$notAvailableText = _ref.notAvailableText,
      notAvailableText = _ref$notAvailableText === void 0 ? 'n/a' : _ref$notAvailableText,
      hideDayCellText = _ref.hideDayCellText,
      theme = _ref.theme;
  var today = startOfToday();
  var datePricing = prices.find(function (p) {
    return p.date === format(date, 'yyyy-MM-dd');
  });
  var priceInfoObj = datePricing === null || datePricing === void 0 ? void 0 : datePricing.pricing;
  var isAvailable = (priceInfoObj === null || priceInfoObj === void 0 ? void 0 : priceInfoObj.availability) === 'AVAILABLE';
  var notAvailableTextLabel = (datePricing === null || datePricing === void 0 ? void 0 : datePricing.notAvailableText) || notAvailableText;
  return React.createElement(DayCell, {
    highLightColor: getColorFromWeight(priceInfoObj === null || priceInfoObj === void 0 ? void 0 : priceInfoObj.weight, theme),
    theme: theme,
    className: 'datepicker'
  }, React.createElement("div", {
    className: 'day datepicker'
  }, date.getDate()), !hideDayCellText && !isBefore(date, today) && React.createElement("div", {
    className: 'price datepicker',
    style: {
      color: getColorFromWeight(isAvailable ? priceInfoObj === null || priceInfoObj === void 0 ? void 0 : priceInfoObj.weight : 0, theme),
      fontWeight: (priceInfoObj === null || priceInfoObj === void 0 ? void 0 : priceInfoObj.weight) === -1 ? 'bold' : undefined
    }
  }, isSameDay(date, today) || !isBefore(date, today) ? React.createElement(React.Fragment, null, isAvailable ? priceInfoObj.price : notAvailableTextLabel) : React.createElement(React.Fragment, null, "\xA0")));
};

var RtpCalendar = function RtpCalendar(_ref) {
  var theme = _ref.theme,
      locale = _ref.locale,
      prices = _ref.prices,
      notAvailableText = _ref.notAvailableText,
      modifiers = _ref.modifiers,
      hideDayCellText = _ref.hideDayCellText,
      disabledDays = _ref.disabledDays,
      fromMonth = _ref.fromMonth,
      toMonth = _ref.toMonth,
      onMonthChange = _ref.onMonthChange,
      onDayClick = _ref.onDayClick,
      month = _ref.month,
      firstDayOfWeek = _ref.firstDayOfWeek;
  var today = startOfToday();

  var _React$useState = React.useState(today),
      selectedDate = _React$useState[0],
      setSelectedDate = _React$useState[1];

  var _React$useState2 = React.useState(new Date()),
      now = _React$useState2[0],
      setNow = _React$useState2[1];

  var renderDayCellFromData = function renderDayCellFromData(date) {
    return React.createElement(RtpCalendarDayCell, {
      date: date,
      prices: prices,
      hideDayCellText: hideDayCellText,
      notAvailableText: notAvailableText,
      theme: theme
    });
  };

  var renderDayCell = function renderDayCell(date) {
    return React.createElement(DayCell, null, React.createElement("div", {
      className: 'day'
    }, date.getDate()));
  };

  var handleOnMonthChange = function handleOnMonthChange(month) {
    if (month.getMonth() !== now.getMonth()) {
      setNow(month);
    }

    onMonthChange && onMonthChange(month);
  };

  var handleOnDayClick = function handleOnDayClick(selected, options) {
    setSelectedDate(selected);
    onDayClick && onDayClick(selected, options);
  };

  var selectedFirstDayOfWeek = typeof firstDayOfWeek === 'number' ? firstDayOfWeek : 1;
  return React.createElement(CalendarStyleWrapper, {
    theme: theme
  }, React.createElement(OrigDatePicker, {
    initialMonth: selectedDate || today,
    locale: locale,
    modifiers: modifiers,
    month: month || now,
    months: MONTHS[locale],
    weekdaysLong: WEEKDAYS_LONG[locale],
    weekdaysShort: WEEKDAYS_SHORT[locale],
    labels: LABELS[locale],
    firstDayOfWeek: selectedFirstDayOfWeek,
    showOutsideDays: true,
    onDayClick: handleOnDayClick,
    onMonthChange: handleOnMonthChange,
    renderDay: !!prices ? renderDayCellFromData : renderDayCell,
    disabledDays: disabledDays,
    fromMonth: fromMonth,
    toMonth: toMonth
  }));
};

export { RtpCalendar };
//# sourceMappingURL=index.modern.js.map
